import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { CourtTemplatePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

export default function CourtTemplatesPage() {
  const [courtTypes, setCourtTypes] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedCourtTypeId, setSelectedCourtTypeId] = useState("");
  const [templates, setTemplates] = useState<CourtTemplatePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CourtTemplatePublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .getCourtTypes()
      .then((res) => {
        setCourtTypes(res.court_types);
        if (res.court_types.length > 0) {
          setSelectedCourtTypeId(res.court_types[0].id);
        }
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError
            ? e.message
            : "Failed to load court types",
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedCourtTypeId) return;
    setLoading(true);
    setError(null);
    api
      .getCourtTemplates(selectedCourtTypeId)
      .then((res) => setTemplates(res.templates))
      .catch((e) =>
        setError(
          e instanceof api.ApiError
            ? e.message
            : "Failed to load templates",
        ),
      )
      .finally(() => setLoading(false));
  }, [selectedCourtTypeId]);

  async function handleDelete(id: string) {
    if (
      !confirm(
        "Are you sure you want to delete this template? All template mounts will be removed.",
      )
    )
      return;
    try {
      await api.deleteCourtTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      setError(
        e instanceof api.ApiError ? e.message : "Failed to delete template",
      );
    }
  }

  return (
    <div className="flex h-full gap-0" data-page="court-templates-list" data-state={loading ? "loading" : error ? "error" : templates.length === 0 ? "empty" : "ready"}>
    <div className="min-w-0 flex-1 flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Court Templates</h1>
          <p className="text-muted-foreground">
            Manage reusable mount layout templates
          </p>
        </div>
        <div className="flex gap-2">
          {selectedCourtTypeId && (
            <Link
              to={`/court-types/${selectedCourtTypeId}/templates/new`}
              className={buttonVariants({ size: "sm" })}
              data-action="create"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Template
            </Link>
          )}
          <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned(p => !p)} />
        </div>
      </div>

      {courtTypes.length > 1 && (
        <div className="flex items-center gap-3">
          <Label htmlFor="court-type-select">Court Type</Label>
          <select
            id="court-type-select"
            value={selectedCourtTypeId}
            onChange={(e) => setSelectedCourtTypeId(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {courtTypes.map((ct) => (
              <option key={ct.id} value={ct.id}>
                {ct.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && <p className="text-muted-foreground">Loading...</p>}

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {!loading && !error && templates.length === 0 && selectedCourtTypeId && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
          <p className="text-muted-foreground">No templates yet</p>
          <Link
            to={`/court-types/${selectedCourtTypeId}/templates/new`}
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            Add your first template
          </Link>
        </div>
      )}

      {!loading && !error && templates.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Court Type</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="w-36" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow
                  key={template.id}
                  className={`cursor-pointer ${selected?.id === template.id ? "bg-muted" : ""}`}
                  onClick={() => setSelected(template)}
                  onDoubleClick={() => navigate(`/court-templates/${template.id}`)}
                  data-item="template"
                  data-item-id={template.id}
                >
                  <TableCell className="font-medium">
                    {template.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {template.court_type_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {template.is_default ? "Yes" : "\u2014"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {template._actions.includes("view") && (
                        <Link
                          to={`/court-templates/${template.id}`}
                          className={buttonVariants({
                            variant: "ghost",
                            size: "sm",
                          })}
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      )}
                      {template._actions.includes("edit") && (
                        <Link
                          to={`/court-templates/${template.id}/edit`}
                          className={buttonVariants({
                            variant: "ghost",
                            size: "sm",
                          })}
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                      )}
                      {template._actions.includes("delete") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDelete(template.id); }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
    <PreviewPane pinned={pinned} onTogglePin={() => setPinned(p => !p)} title="Court Template">
      {selected && (
        <>
          <PreviewField label="Name">{selected.name}</PreviewField>
          <PreviewField label="Court Type">{selected.court_type_name}</PreviewField>
          <PreviewField label="Default">{selected.is_default ? "Yes" : "No"}</PreviewField>
        </>
      )}
    </PreviewPane>
    </div>
  );
}
